import { useEffect } from 'react';

interface PaymentRedirectPageProps {
  /** Variant visual: 'success' (verde) ou 'pending' (azul). */
  variant: 'success' | 'pending';
  /** Titulo grande no topo do card. */
  title: string;
  /** Subtitulo / paragrafo descritivo abaixo do titulo. */
  message: string;
  /** Label do CTA (botao principal). */
  ctaLabel?: string;
}

function CheckIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Pagina de redirect pos-pagamento (AbacatePay).
 *
 * Usada nas rotas:
 *   /completion — variant='success'  (pagamento recebido)
 *   /return     — variant='pending'  (pagamento nao finalizado)
 *
 * Minimalista: SEM header/footer/menu — o usuario chega aqui via redirect
 * do gateway. CTA tenta fechar a aba; se o browser bloquear (aba aberta
 * manualmente, sem JS opener), redireciona pra home como fallback.
 */
export function PaymentRedirectPage({
  variant,
  title,
  message,
  ctaLabel = 'Fechar',
}: PaymentRedirectPageProps) {
  // Injeta <meta name="robots" content="noindex, nofollow"> dinamicamente no
  // <head> enquanto a pagina estiver montada. Defesa em profundidade — o
  // header HTTP X-Robots-Tag (staticwebapp.config.json) ja faz o trabalho
  // principal, mas o meta tag protege caso o header se perca por proxy/cache.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const handleClose = () => {
    window.close();
    // Se window.close() teve sucesso, a pagina ja foi descarregada e este
    // setTimeout nunca dispara. Se foi bloqueado (browser nao permite fechar
    // abas nao abertas por script), o setTimeout dispara e leva pra home.
    // 200ms eh tempo suficiente pro browser processar close() ou ignorar.
    setTimeout(() => {
      window.location.href = '/';
    }, 200);
  };

  return (
    <main className={`pay-redirect pay-redirect--${variant}`}>
      <section className="pay-redirect__card">
        <span className="pay-redirect__icon" aria-hidden="true">
          {variant === 'success' ? <CheckIcon /> : <ClockIcon />}
        </span>
        <h1 className="pay-redirect__title">{title}</h1>
        <p className="pay-redirect__message">{message}</p>
        <button
          type="button"
          className="pay-redirect__cta"
          onClick={handleClose}
        >
          <CloseIcon />
          <span>{ctaLabel}</span>
        </button>
      </section>
    </main>
  );
}
